import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OnboardingAdjusting from '../../../assets/OnboardingAdjusting.svg?react';
import OnboardingAutoposition from '../../../assets/OnboardingAutoposition.svg?react';
import OnboardingLuminiares from '../../../assets/OnboardingLuminiares.svg?react';
import OnboardingMarketMatch from '../../../assets/OnboardingMarketMatch.svg?react';
import OnboardingMarketMatchLogo from '../../../assets/OnboardingMarketMatchLogo.svg?react';
import { useAppDispatch } from '../../../hooks.ts';
import { userReducer } from '../../../store/reducers/user.reducer.ts';

interface OnboardingDefinition {
  label: string;
  text: string;
}

interface OnboardingStep {
  title: string;
  icon: React.ReactNode;
  mainTitle: string;
  description: string;
  /** Optional list of bold-label / text definitions rendered under the intro, separated by hairline dividers. */
  definitions?: OnboardingDefinition[];
  /** Optional footer note — rendered dimmed at the very bottom. Used for "optional" extras (e.g. layer types for better rendering). */
  footerNote?: string;
  mainTitleSecond?: string;
  descriptionSecond?: string;
  mainTitleThird?: string;
  descriptionThird?: string;
}

const { updateHideOnboarding } = userReducer.actions;

// Toggle switches illustration for step 2
const ToggleSwitchesIllustration: React.FC = () => (
  <div className="flex justify-center gap-5 my-4">
    {/* CHL Toggle - Active */}
    <div className="flex flex-col items-center">
      <span className="mb-2 text-sm font-bold text-gray-800">CHL</span>
      <svg width="60" height="34" viewBox="0 0 60 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="34" rx="17" fill="#E5E7EB" />
        <circle cx="43" cy="17" r="13" fill="#1F2937" />
      </svg>
    </div>

    {/* Lmn Toggle - Inactive */}
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-1 mb-2 text-sm font-bold text-gray-400">
        <span>Lmn</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          className="w-4 h-4"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
          />
        </svg>
      </div>
      <svg width="60" height="34" viewBox="0 0 60 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="58" height="32" rx="16" stroke="#9CA3AF" strokeWidth="2" />
        <circle cx="17" cy="17" r="12" stroke="#9CA3AF" strokeWidth="2" />
      </svg>
    </div>
  </div>
);

interface OnboardingPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OnboardingPopup: React.FC<OnboardingPopupProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(0);

  const ONBOARDING_STEPS: OnboardingStep[] = useMemo(
    () => [
      {
        title: t('onboarding.steps.setup.title'),
        icon: <OnboardingAdjusting className="w-5 h-5 text-black" />,
        mainTitle: t('onboarding.steps.setup.mainTitle'),
        description: t('onboarding.steps.setup.description'),
        definitions: [
          {
            label: t('onboarding.steps.setup.maintenanceLabel'),
            text: t('onboarding.steps.setup.maintenanceText'),
          },
          {
            label: t('onboarding.steps.setup.unitsLabel'),
            text: t('onboarding.steps.setup.unitsText'),
          },
          {
            label: t('onboarding.steps.setup.modelScaleLabel'),
            text: t('onboarding.steps.setup.modelScaleText'),
          },
        ],
        mainTitleThird: t('onboarding.steps.setup.examplesTitle'),
        descriptionThird: t('onboarding.steps.setup.examplesDescription'),
      },
      {
        title: t('onboarding.steps.layers.title'),
        icon: <OnboardingAdjusting className="w-5 h-5 text-black" />,
        mainTitle: t('onboarding.steps.layers.mainTitle'),
        description: t('onboarding.steps.layers.description'),
        mainTitleSecond: t('onboarding.steps.layers.chlTitle'),
        descriptionSecond: t('onboarding.steps.layers.chlDescription'),
        mainTitleThird: t('onboarding.steps.layers.lmnTitle'),
        descriptionThird: t('onboarding.steps.layers.lmnDescription'),
        footerNote: t('onboarding.steps.layers.typeNote'),
      },
      {
        title: t('onboarding.steps.autoposition.title'),
        icon: <OnboardingLuminiares className="w-5 h-5 text-lime-400" />,
        mainTitle: t('onboarding.steps.autoposition.mainTitle'),
        description: t('onboarding.steps.autoposition.description'),
        mainTitleThird: t('onboarding.steps.autoposition.optimizationTitle'),
        descriptionThird: t('onboarding.steps.autoposition.optimizationDescription'),
      },
      {
        title: t('onboarding.steps.marketMatch.title'),
        icon: <OnboardingMarketMatchLogo className="w-[18px] h-[18px]" />,
        mainTitle: t('onboarding.steps.marketMatch.mainTitle'),
        description: t('onboarding.steps.marketMatch.description'),
        mainTitleThird: t('onboarding.steps.marketMatch.compatibilityTitle'),
        descriptionThird: t('onboarding.steps.marketMatch.compatibilityDescription'),
      },
    ],
    [t],
  );

  const handleNextStep = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handleDontShowAgain = () => {
    dispatch(updateHideOnboarding());
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  const step = ONBOARDING_STEPS[currentStep];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div
        className="relative bg-white w-[350px] max-w-[90vw] h-[600px] max-h-[90vh] shadow-2xl flex flex-col"
        style={{ borderRadius: '6px' }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-[#232323] hover:text-[#747474] transition rounded-full hover:bg-gray-100 z-10"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Header */}
        <div className="px-4 pt-8 pb-3">
          <div className="flex items-center justify-start mb-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#747474]">
                {t('onboarding.stepCount', { current: currentStep + 1, total: ONBOARDING_STEPS.length })}
              </span>
              <span className="text-[#232323] font-medium text-xs">{step.title}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex gap-1.5">
            {ONBOARDING_STEPS.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                  index <= currentStep ? 'bg-lime-400' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content - overflow-y-auto to handle long text */}
        <div
          className="px-4 py-4 flex-grow overflow-y-auto bg-[#F5F5F5] onboarding-scroll"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#B5B5B5 #F5F5F5',
          }}
        >
          {/* Icon and Main Content - Centered */}
          <div className="mb-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0">{step.icon}</div>
              <h2 className="text-[20px] text-[#232323]">{step.mainTitle}</h2>
            </div>
            <p className="text-[#232323] text-[11px] leading-relaxed">{step.description}</p>
          </div>

          {/* Bold-label definitions (e.g. Maintenance Factor / Units / Model scale on step 1) */}
          {step.definitions && step.definitions.length > 0 && (
            <div className="mb-4">
              <div className="h-px bg-gray-200 mb-3" />
              {step.definitions.map((def, idx) => (
                <div key={def.label}>
                  <p className="text-[11px] leading-relaxed">
                    <span className="font-semibold text-[#232323]">{def.label}</span>{' '}
                    <span className="text-[#232323]">- {def.text}</span>
                  </p>
                  {idx < step.definitions!.length - 1 && <div className="h-px bg-gray-200 my-3" />}
                </div>
              ))}
            </div>
          )}

          {/* Toggle Switches Illustration - Only show on step 2 */}
          {currentStep === 1 && <ToggleSwitchesIllustration />}

          {currentStep === 2 && (
            <div className="flex items-center justify-center gap-2">
              <OnboardingAutoposition />
            </div>
          )}

          {currentStep === 3 && (
            <div className="flex items-center justify-center gap-2 my-2">
              <OnboardingMarketMatch className="w-32 h-32" />
            </div>
          )}

          {step.mainTitleSecond && (
            <div className="mb-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <h2 className="text-[20px] text-[#232323]">{step.mainTitleSecond}</h2>
              </div>
              <p className="text-[#232323] text-[11px] leading-relaxed">{step.descriptionSecond}</p>
            </div>
          )}

          {step.mainTitleThird && (
            <>
              {/* Divider */}
              <div className="h-px bg-gray-200 my-4" />

              <div className="mb-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h2 className="text-[20px] text-[#232323]">{step.mainTitleThird}</h2>
                </div>
                <p className="text-[#232323] text-[11px] leading-relaxed">{step.descriptionThird}</p>
              </div>
            </>
          )}

          {/* Optional footer note — dimmed, used for "optional" tips (e.g. layer types for better renders) */}
          {step.footerNote && (
            <>
              <div className="h-px bg-gray-200 my-3" />
              <p className="text-[10px] leading-relaxed text-[#747474] italic text-center">{step.footerNote}</p>
            </>
          )}
        </div>

        {/* Footer - Always at bottom */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between mt-auto">
          <button onClick={handleDontShowAgain} className="text-[10px] text-[#747474] hover:text-[#232323] transition">
            {t('onboarding.dontShowAgain')}
          </button>

          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 text-[#747474] font-medium text-[12px] rounded-full hover:bg-gray-50 hover:text-[#232323] transition"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 4L6 8L10 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t('onboarding.back', 'Back')}
              </button>
            )}

            <button
              onClick={handleNextStep}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#747474] text-[#232323] font-medium text-[12px] rounded-full hover:bg-gray-50 transition"
            >
              {currentStep < ONBOARDING_STEPS.length - 1 ? (
                <>
                  {t('onboarding.nextStep')}
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 4L10 8L6 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              ) : (
                t('onboarding.getStarted')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
